import { useState } from "react";
import { HelpContentContainer } from ".";
import { Button } from "@/components/Button/Button";
import { ModalHeader } from "@/components/Modal/ModalHeader";
import { AppText } from "@/components/Text/AppText";
import { sendFeedbackEmail } from "@/email/send";
import { useAppSelector } from "@/hooks/types";
import { getTenant, getIs1TemplateTenant } from "@/store/tenant/selectors";
import { getUserDetails } from "@/store/user/selectors";

interface HelpGuideContactProps {
    onClose: () => void;
}

export const HelpGuideContact = ({ onClose }: HelpGuideContactProps) => {
    const [feedback, setFeedback] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState<{
        type: "success" | "error";
        message: string;
    } | null>(null);
    const user = useAppSelector(getUserDetails);
    const tenant = useAppSelector(getTenant);
    const is1TemplateTenant = useAppSelector(getIs1TemplateTenant);

    const handleSubmitFeedback = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            await sendFeedbackEmail(
                tenant,
                feedback,
                window.location.href,
                user,
            );
            setFeedback("");
            setSubmitStatus({
                type: "success",
                message: "Thank you for your feedback!",
            });
        } catch (error) {
            setSubmitStatus({
                type: "error",
                message: "Failed to send feedback. Please try again.",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div>
            <ModalHeader
                title="Contact Us"
                onClose={onClose}
            />
            <HelpContentContainer>
                <AppText variant="regular">
                    For any inquiries, please reach out to us at:
                </AppText>
                <a href={`mailto:${tenant?.contactEmail}`}>
                    <AppText
                        variant="labelsbuttons"
                        className="text-primary-500 underline hover:text-primary-600"
                    >
                        {tenant?.contactEmail}
                    </AppText>
                </a>
                {is1TemplateTenant && (
                    <form
                        onSubmit={handleSubmitFeedback}
                        className="mt-4 space-y-4"
                    >
                        <div>
                            <AppText
                                variant="regular"
                                className="mb-2"
                            >
                                Or send us feedback directly:
                            </AppText>
                            <textarea
                                value={feedback}
                                onChange={e => setFeedback(e.target.value)}
                                className="w-full rounded-md border border-gray-300 p-2"
                                rows={4}
                            />
                        </div>
                        <Button
                            type="submit"
                            disabled={isSubmitting || !feedback.trim()}
                            color={"primary"}
                        >
                            {isSubmitting ? "Sending..." : "Send Feedback"}
                        </Button>
                        {submitStatus && (
                            <AppText
                                variant="regular"
                                className={
                                    submitStatus.type === "success"
                                        ? "text-success-600"
                                        : "text-error-600"
                                }
                            >
                                {submitStatus.message}
                            </AppText>
                        )}
                    </form>
                )}
            </HelpContentContainer>
        </div>
    );
};
